import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  SvgIconTypeMap,
  Tooltip,
  Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import BedtimeRoundedIcon from '@mui/icons-material/BedtimeRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import moment from 'moment';
import { MessageFrom } from 'pages/user/components/MessageFrom/MessageFrom';
import { MessageTo } from 'pages/user/components/MessageTo/MessageTo';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  ConversationPartModel,
  ConversationPartTypeEnum,
  getConverstationAsync,
  handleTag,
  refreshConverstationAsync,
  TagAiPartModel,
} from 'store/intercomSlice';
import { TriageModel } from 'data/api/triage.api';
import {
  ConversationStatusEvent,
  InboxStatus,
} from 'data/api/conversation.api';
import {
  mergeThreadItems,
  messagesToThreadItems,
  statusEventsToThreadItems,
  ThreadItem,
  triagesToThreadItems,
} from './threadItems';
import { calendarTime } from './snooze';
import ModalCustom from 'components/Modal';
import { removeMessageTag } from 'data/api/conversation.api';
import { selectAccount } from 'store/accountSlide';
import { toast } from 'react-toastify';

const STAFF_BUBBLE_INSET = '39px';

// Written by their own flows (the triage form, the Intercom tag sync), not the
// AI. The API refuses to remove them, so they get no control.
const NON_AI_TAG_NAMES = ['Triage', 'Review Requested'];

const STATUS_LOOK: Record<
  InboxStatus,
  {
    Icon: OverridableComponent<SvgIconTypeMap>;
    tint: string;
    color: string;
  }
> = {
  snoozed: { Icon: BedtimeRoundedIcon, tint: '#EFEAE4', color: '#5C534A' },
  open: { Icon: ReplayRoundedIcon, tint: '#E4E9F5', color: '#3D5A99' },
  closed: { Icon: CheckRoundedIcon, tint: '#E3EFEA', color: '#356766' },
};

const RULE = { flex: 1, minWidth: '16px', height: '1px', bgcolor: '#DBD8D8' };

const isStaffAuthored = (part: ConversationPartModel) =>
  part?.conversationAuthorType === ConversationPartTypeEnum.admin ||
  part?.conversationAuthorType === ConversationPartTypeEnum.bot;

interface IConversationThreadProps {
  /** The member whose conversation to show; the store is keyed by user, not conversation. */
  userId?: string;
  /**
   * AI tag chips + jump-to-message navigation. On the member tab this stays
   * on (it is an existing feature); Sam's inbox design has no message tags,
   * so the inbox pane turns it off. Open question #3 on the design read-out.
   */
  showTags?: boolean;
  /**
   * The member's triages, rendered inline among the messages by the date they
   * were raised (BL-TASK-1419). Fetched once by the inbox page and shared
   * with the context rail, so opening a conversation costs one triage read,
   * not two.
   */
  triages?: TriageModel[];
  /** Opens a triage's detail in the rail. Omitted, the cards are inert. */
  onOpenTriage?: (triagesId: string) => void;
  /**
   * The family's open/snoozed/closed changes (BL-TASK-1444), rendered as
   * markers across the thread. Owner-guarded by the inbox page's hook.
   */
  statusEvents?: ConversationStatusEvent[];
}

export const ConversationThread: React.FC<IConversationThreadProps> = ({
  userId,
  showTags = true,
  triages,
  onOpenTriage,
  statusEvents,
}) => {
  const dispatch = useAppDispatch();
  const stored = useAppSelector((state) => state.intercom.converstation);
  const loadedUserId = useAppSelector((state) => state.intercom.loadedUserId);
  const requestedUserId = useAppSelector(
    (state) => state.intercom.requestedUserId,
  );
  const loadStatus = useAppSelector((state) => state.intercom.getConversations);
  const storeTag = useAppSelector((state) => state.intercom.tag);
  // The store's tag selection belongs to the member tab. With tags hidden
  // there is no chip showing what is selected, so a tag left over from that
  // tab must not suppress scroll-to-bottom or jump the inbox thread to a
  // match the rep never asked for.
  const activeTag = showTags ? storeTag : '';
  // The removal route is platform-admin only; undefined while the account
  // loads, so the control stays hidden until we know.
  const account = useAppSelector(selectAccount);
  const canRemoveTags = showTags && !!account?.isPlatformAdmin;
  // Kept until the dialog has finished closing, so its title doesn't blank
  // mid-fade; `removalOpen` is what opens and closes it.
  const [tagRemoval, setTagRemoval] = useState<{
    tag: string;
    mentionTagIds: string[];
    itemId: string;
  } | null>(null);
  const [removalOpen, setRemovalOpen] = useState(false);
  const [removingTag, setRemovingTag] = useState(false);
  // The message to focus once the dialog has closed: the × that opened it is
  // gone, and focus would otherwise drop to the top of the page.
  const focusAfterRemovalRef = useRef<string>();

  // Never render a transcript that belongs to someone else. Selecting a new
  // row re-renders with the new header before the fetch effect has run, so
  // for one commit the store still holds the previous member's messages —
  // this is a PHI surface, so that single frame matters. The failure state
  // keys off the requested member instead, since a failed load has no
  // transcript to own.
  const isOwned = !!userId && loadedUserId === userId;
  const failedForThisMember =
    !!userId && requestedUserId === userId && loadStatus === 'failed';
  const conversation = isOwned ? stored : null;
  const loading = !!userId && !isOwned && !failedForThisMember;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const [matchIndex, setMatchIndex] = useState<number>(0);
  // Whether the reader is at the newest message. The inbox re-reads the
  // thread in the background; new content is followed only from the bottom,
  // so someone reading older messages keeps their place.
  const atBottomRef = useRef(true);
  const scrolledForRef = useRef<string>();

  // What is on screen now, read after the awaits below: the admin can move to
  // another member while a removal is in flight.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const activeTagRef = useRef(activeTag);
  activeTagRef.current = activeTag;

  const confirmTagRemoval = async () => {
    if (!tagRemoval || !userId) return;
    const forUserId = userId;
    setRemovingTag(true);
    let removed = false;
    try {
      await removeMessageTag(tagRemoval.mentionTagIds);
      removed = true;
    } catch {
      toast.error('Could not remove the tag. Please try again.');
    }
    // Close onto the thread as it now is, success or not: a tag saved twice
    // can be half removed, and a retry has to start from the ids that are left.
    const refreshed = await dispatch(refreshConverstationAsync(forUserId));
    setRemovingTag(false);
    setRemovalOpen(false);
    if (!removed || userIdRef.current !== forUserId) return;
    focusAfterRemovalRef.current = tagRemoval.itemId;
    // Only a re-read that came back can say the tag is gone; its chip is then
    // gone too, and nothing on screen could clear the selection.
    const tagGone =
      refreshConverstationAsync.fulfilled.match(refreshed) &&
      !refreshed.payload.conversation?.tagAiPart?.some(
        (t) => t.tagName === tagRemoval.tag,
      );
    if (tagGone && activeTagRef.current === tagRemoval.tag) {
      dispatch(handleTag(''));
    }
  };

  const items: ThreadItem[] = useMemo(
    () =>
      mergeThreadItems(
        messagesToThreadItems(conversation?.conversationPart),
        // Only ever this member's: the rail and the thread read the same
        // owner-guarded list, so a triage load that lands after the selection
        // moved on has nothing to render against.
        isOwned ? triagesToThreadItems(triages) : [],
        isOwned ? statusEventsToThreadItems(statusEvents) : [],
      ),
    [conversation, triages, statusEvents, isOwned],
  );

  const itemRefs = useMemo(() => {
    const refs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
    items.forEach((item) => {
      refs[item.id] = React.createRef<HTMLDivElement>();
    });
    return refs;
  }, [items]);

  // Ids of the messages carrying the active tag, in thread order. The old
  // implementation derived a scroll offset from summed clientHeights and a
  // magic /6.5 divisor; the ref is enough.
  const matchingIds = useMemo(() => {
    if (!activeTag) return [] as string[];
    return items
      .filter(
        (item) =>
          item.kind === 'message' &&
          // AI tags are only attached to member messages; matching staff
          // replies too would change the "n of m" counts the chips show.
          item.part.conversationAuthorType === ConversationPartTypeEnum.user &&
          item.part.tags?.includes(activeTag),
      )
      .map((item) => item.id);
  }, [items, activeTag]);

  useEffect(() => {
    if (userId) dispatch(getConverstationAsync(userId));
  }, [userId]);

  // A tag selection belongs to the thread it was made in, so switching
  // threads clears it. Only on an actual switch, never on first mount: the
  // member list, user search and company-employees pages dispatch handleTag
  // and *then* navigate here, and clearing on mount would drop that.
  const previousUserId = useRef<string | undefined>(userId);
  useEffect(() => {
    if (
      previousUserId.current !== undefined &&
      previousUserId.current !== userId
    ) {
      setMatchIndex(0);
      // Only the tag-showing thread owns the store's selection; the inbox
      // pane switching rows must not clear what the member tab set.
      if (showTags) dispatch(handleTag(''));
    }
    previousUserId.current = userId;
  }, [userId]);

  useEffect(() => {
    if (activeTag || !items.length) return;
    if (scrolledForRef.current === userId && !atBottomRef.current) return;
    scrolledForRef.current = userId;
    atBottomRef.current = true;
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [items, activeTag]);

  useEffect(() => {
    const targetId = matchingIds[matchIndex];
    if (!targetId) return;
    itemRefs[targetId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [matchingIds, matchIndex, itemRefs]);

  const handleTagClick = (tagName: string) => {
    if (activeTag === tagName) {
      // Same chip again advances through its matches and wraps.
      setMatchIndex((current) =>
        matchingIds.length ? (current + 1) % matchingIds.length : 0,
      );
      return;
    }
    setMatchIndex(0);
    dispatch(handleTag(tagName));
  };

  // A removal can take away the match being shown; stay on the last one.
  useEffect(() => {
    if (matchingIds.length && matchIndex >= matchingIds.length) {
      setMatchIndex(matchingIds.length - 1);
    }
  }, [matchingIds]);

  const activeMatchId = matchingIds[matchIndex];
  const hasParts = !!conversation?.conversationPart?.length;

  const renderItem = (item: ThreadItem) => {
    if (item.kind === 'status') {
      const { Icon, tint, color } = STATUS_LOOK[item.status];
      const now = moment();
      const byline = [
        item.actor && `by ${item.actor}`,
        item.at && calendarTime(moment.unix(item.at), now),
      ]
        .filter(Boolean)
        .join(' · ');
      return (
        <Stack
          key={item.id}
          ref={itemRefs[item.id]}
          direction="row"
          alignItems="center"
          gap="12px"
        >
          <Box sx={RULE} />
          <Tooltip
            title={
              item.status === 'snoozed' && item.snoozedUntil
                ? `Until ${calendarTime(moment(item.snoozedUntil), now)}`
                : ''
            }
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                minWidth: 0,
                border: '1px solid #E4E1DE',
                borderRadius: '999px',
                backgroundColor: '#FFFFFF',
                padding: '4px 16px 4px 4px',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  backgroundColor: tint,
                  color,
                }}
              >
                <Icon sx={{ fontSize: '16px' }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#213B54',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
              <Typography noWrap sx={{ fontSize: '13px', color: '#7D818C' }}>
                {byline}
              </Typography>
            </Stack>
          </Tooltip>
          <Box sx={RULE} />
        </Stack>
      );
    }

    if (item.kind === 'event') {
      const open = onOpenTriage ? () => onOpenTriage(item.id) : undefined;
      return (
        <Stack
          key={item.id}
          ref={itemRefs[item.id]}
          direction="row"
          alignItems="flex-start"
          gap={1}
          role={open ? 'button' : undefined}
          tabIndex={open ? 0 : undefined}
          onClick={open}
          onKeyDown={(event) => {
            if (!open) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              open();
            }
          }}
          // Right edge lines up with the staff bubbles, which sit inset by
          // MessageTo's padding + gap + 23px avatar. Inline because the
          // thread Stack's spacing resets child margins above sx specificity.
          style={{ marginRight: STAFF_BUBBLE_INSET }}
          sx={{
            alignSelf: 'flex-end',
            width: `calc(100% - ${STAFF_BUBBLE_INSET})`,
            maxWidth: '440px',
            border: '1px solid #DBD8D8',
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            padding: '10px 12px',
            cursor: open ? 'pointer' : 'default',
            ':hover': open ? { borderColor: '#356766' } : undefined,
          }}
        >
          <NorthEastRoundedIcon sx={{ color: '#356766', fontSize: '18px' }} />
          <Stack>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600, color: '#213B54' }}
            >
              {item.label}
            </Typography>
            {!!item.detail && (
              <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                {item.detail}
              </Typography>
            )}
          </Stack>
        </Stack>
      );
    }

    const part = item.part;
    const bubbleProps = {
      body: part?.body,
      time: moment.unix(part?.createdAt || 0).format('MM/DD/YYYY hh:mm A'),
      avatar: part?.avatar || '',
      authorName: part?.authorName || '',
      tags: showTags ? part.tags : undefined,
      conversationId: part.conversationPartId,
      conversationPartIdRef: activeMatchId || '',
      tagCurrent: showTags ? activeTag : '',
    };
    if (isStaffAuthored(part)) {
      return (
        <div key={item.id} ref={itemRefs[item.id]}>
          <MessageTo {...bubbleProps} />
        </div>
      );
    }
    const removable = canRemoveTags
      ? (part.mentionTags ?? []).filter(
          (t) => !NON_AI_TAG_NAMES.includes(t.name),
        )
      : [];
    return (
      <div key={item.id} ref={itemRefs[item.id]} tabIndex={-1}>
        <MessageFrom
          {...bubbleProps}
          removableTags={removable.map((t) => t.name)}
          onRemoveTag={(tag) => {
            const entry = removable.find((t) => t.name === tag);
            if (!entry) return;
            setTagRemoval({
              tag,
              mentionTagIds: entry.mentionTagIds,
              itemId: item.id,
            });
            setRemovalOpen(true);
          }}
        />
      </div>
    );
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      {showTags && !!conversation?.tagAiPart?.length && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            flexWrap: 'wrap',
            color: '#7D818C',
            fontWeight: 700,
            fontSize: '13px',
            paddingBottom: '12px',
          }}
        >
          Tags:
          {conversation?.tagAiPart?.map((tag: TagAiPartModel) => {
            const name = tag.tagName || '';
            const selected = activeTag === name;
            return (
              <Stack key={name} sx={{ position: 'relative' }}>
                <Typography
                  component="span"
                  onClick={() => handleTagClick(name)}
                  sx={{
                    color: selected ? 'rgba(33, 59, 84, 1)' : '#356766',
                    backgroundColor: '#3567661A',
                    border: selected
                      ? '2px solid rgba(33, 59, 84, 1)'
                      : '1px solid #356766',
                    borderRadius: '20px',
                    paddingX: '8px',
                    height: '24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    width: 'fit-content',
                    cursor: 'pointer',
                    ':hover': { backgroundColor: 'rgba(53, 103, 102, 0.25)' },
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  sx={{
                    position: 'absolute',
                    right: -8,
                    bottom: -4,
                    borderRadius: 100,
                    backgroundColor: selected
                      ? 'rgba(33, 59, 84, 1)'
                      : 'rgba(53, 103, 102, 1)',
                    height: '18px',
                    width: selected ? 'auto' : '18px',
                    paddingX: '4px',
                    textAlign: 'center',
                    fontSize: '12px',
                    lineHeight: '18px',
                    fontWeight: 700,
                    color: '#FFFFFF',
                  }}
                >
                  {selected && matchingIds.length
                    ? `${matchIndex + 1} of ${matchingIds.length}`
                    : tag.total}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}

      <Stack
        ref={scrollRef}
        onScroll={() => {
          const el = scrollRef.current;
          if (!el) return;
          atBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
        spacing={2}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px',
          backgroundColor: items.length ? '#F7F7F7' : 'transparent',
          borderTop: '1px solid #9999997A',
        }}
      >
        {loading && (
          <Stack alignItems="center" paddingY="48px">
            <CircularProgress size={28} sx={{ color: '#356766' }} />
          </Stack>
        )}

        {failedForThisMember && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            paddingY="48px"
          >
            <ForumOutlinedIcon sx={{ color: '#7D818C', fontSize: '36px' }} />
            <Typography
              sx={{ color: '#213B54', fontWeight: 600, fontSize: '15px' }}
            >
              Could not load conversations
            </Typography>
            <Button
              onClick={() => userId && dispatch(getConverstationAsync(userId))}
              sx={{ color: '#356766', textTransform: 'none', fontWeight: 600 }}
            >
              Try again
            </Button>
          </Stack>
        )}

        {isOwned && loadStatus === 'success' && !hasParts && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            paddingY="48px"
          >
            <ForumOutlinedIcon sx={{ color: '#7D818C', fontSize: '36px' }} />
            <Typography
              sx={{ color: '#213B54', fontWeight: 600, fontSize: '15px' }}
            >
              No messages synced for this member yet
            </Typography>
            <Typography sx={{ color: '#7D818C', fontSize: '14px' }}>
              Conversations will appear here once Intercom messages sync.
            </Typography>
          </Stack>
        )}

        {!loading && items.map(renderItem)}
        <div ref={endRef} />
      </Stack>
      <ModalCustom
        open={removalOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        TransitionProps={{
          onExited: () => {
            const focusId = focusAfterRemovalRef.current;
            focusAfterRemovalRef.current = undefined;
            if (focusId) itemRefs[focusId]?.current?.focus();
            setTagRemoval(null);
          },
        }}
        title={`Remove “${tagRemoval?.tag ?? ''}” from this message?`}
        subTittle="The tag comes off this message and out of the tag counts. The rest of the conversation keeps its tags."
        handleClose={() => !removingTag && setRemovalOpen(false)}
        groupButton={
          <>
            <Button
              onClick={() => setRemovalOpen(false)}
              disabled={removingTag}
              variant="outlined"
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '20px',
                border: '1px solid #213B54',
                height: '40px',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmTagRemoval}
              disabled={removingTag}
              variant="contained"
              color="error"
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '20px',
                height: '40px',
                minWidth: '120px',
              }}
            >
              {removingTag ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                'Remove tag'
              )}
            </Button>
          </>
        }
      />
    </Stack>
  );
};
